import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Waypoint } from 'react-waypoint';

import { FacebookRegularIcon, InstagramIcon, RedditIcon } from '@change/ds-icons';

import { Button, Chip, SocialIcon, Text } from '@change/ds-core-ui/next';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { PostPreview } from 'src/app/pages/starterDashboardV2/shared/components';
import type { PlatformType } from 'src/app/pages/starterDashboardV2/shared/contexts';
import {
	extractMessage,
	mapToSharingModalProps,
	useMarkModuleAsLoaded,
	useSharingModal,
} from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import { ScrollButtons } from '../shared/ScrollButtons';

import { useReadyToSharePreDrafted } from './hooks/useReadyToSharePreDrafted';
import { AVAILABLE_PLATFORMS, PLATFORM_LABELS, type ReadyToSharePost } from './utils/platformGrouping';

// Map platforms to their corresponding icon configurations
const PLATFORM_ICONS: Record<PlatformType, React.ComponentType<{ svgClassName?: string }>> = {
	facebook: FacebookRegularIcon,
	reddit: RedditIcon,
	instagram: InstagramIcon,
	facebookgroups: FacebookRegularIcon,
};

// Helper function to generate button text based on platform and disabled state
const getButtonText = (platform: PlatformType, isDisabled: boolean): string => {
	if (isDisabled) {
		return 'Share from a mobile device';
	}
	const platformLabel = PLATFORM_LABELS[platform];
	return `Post on ${platformLabel}`;
};

// Helper function to map platform to valid SocialIcon variant
const getPlatformVariant = (platform: PlatformType): 'facebook' | 'instagram' | 'reddit' => {
	if (platform === 'facebookgroups') return 'facebook';
	return platform;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export function ReadyToSharePreDrafted(): JSX.Element | null {
	const {
		data,
		actions: { trackView },
	} = useReadyToSharePreDrafted();

	const {
		data: { petition },
	} = useStarterDashboardV2PageData();

	const { startSharingFlow } = useSharingModal();
	const isMobile = useIsMobile();

	// State management for selected platform (null means show all platforms)
	const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | null>(null);

	// Scroll container management
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// Function to update scroll button states
	const updateScrollState = () => {
		const container = scrollContainerRef.current;
		if (!container) return;

		setCanScrollLeft(container.scrollLeft > 0);
		setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
	};

	// Update scroll state when content or platform changes
	useEffect(() => {
		updateScrollState();
		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener('scroll', updateScrollState);
			window.addEventListener('resize', updateScrollState);
			return () => {
				container.removeEventListener('scroll', updateScrollState);
				window.removeEventListener('resize', updateScrollState);
			};
		}
		return undefined;
	}, [selectedPlatform]);

	// Scroll handlers
	const handleScroll = (direction: 'left' | 'right') => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scrollAmount = 300; // Scroll by this many pixels
		const newScrollLeft = container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
		container.scrollTo({
			left: newScrollLeft,
			behavior: 'smooth',
		});
	};

	useMarkModuleAsLoaded('ReadyToSharePreDrafted', data.shouldShowReadyToShareExperiment);

	if (!data.shouldShowReadyToShareExperiment) {
		return null;
	}

	// Filter posts based on selected platform
	const displayedPlatforms = selectedPlatform
		? { [selectedPlatform]: data.groupedPosts[selectedPlatform] }
		: data.groupedPosts;

	// Determine if a post should be disabled (stories on desktop)
	const isPostDisabled = (post: ReadyToSharePost): boolean => {
		const isStory = post.postTypeName.toLowerCase() === 'story';
		return !isMobile && isStory;
	};

	// Handle chip click - toggle selection
	const handleChipClick = (platform: PlatformType) => {
		setSelectedPlatform((current) => (current === platform ? null : platform));
	};

	// Get variant for a platform
	const getPostVariant = (platform: string) => {
		switch (platform) {
			case 'reddit':
				return 'reddit_group_post';
			case 'facebook':
				return 'facebook_post';
			case 'instagram':
				return 'instagram_post';
			case 'facebookgroups':
				return 'facebook_group_post';
			default:
				return 'facebook_post';
		}
	};

	return (
		<section className="bg-background-base flex h-full w-full flex-col px-4 py-8 md:px-12 md:pb-12 md:pt-8">
			<Waypoint onPositionChange={(wp: WaypointCallbackArgs) => trackView(wp.currentPosition)} />

			<div className="mb-5">
				<Text className="text-size-heading-xs md:text-size-heading-small">Ready-to-share posts</Text>
			</div>
			<div className="flex justify-between align-middle">
				{/* Platform Filter Chips and Scroll Controls */}
				<div
					className="mb-6 flex w-full flex-row items-center justify-between gap-4"
					data-testid="chips-and-scroll-row"
				>
					{/* Chips Container */}
					<div className="flex flex-grow gap-2" data-testid="platform-chips-container">
						{/* "All" Chip */}
						<div className="flex flex-grow gap-2">
							<Chip
								active={selectedPlatform === null}
								onClick={() => setSelectedPlatform(null)}
								size="small"
								data-testid="platform-chip-all"
							>
								All
							</Chip>

							{/* Platform Chips */}
							{AVAILABLE_PLATFORMS.map((platform) => {
								const Icon = PLATFORM_ICONS[platform];
								return (
									<Chip
										key={platform}
										className="text-white"
										active={selectedPlatform === platform}
										onClick={() => handleChipClick(platform)}
										size="small"
										leadingIcon={
											<SocialIcon
												className="!text-white"
												size="small"
												icon={<Icon svgClassName="!text-white" />}
												variant={getPlatformVariant(platform)}
											/>
										}
										data-testid={`platform-chip-${platform}`}
									>
										{PLATFORM_LABELS[platform]}
									</Chip>
								);
							})}
						</div>
					</div>
				</div>
				{/* Scroll Buttons */}
				<ScrollButtons
					canScrollLeft={canScrollLeft}
					canScrollRight={canScrollRight}
					onScrollLeft={() => handleScroll('left')}
					onScrollRight={() => handleScroll('right')}
				/>
			</div>

			{/* Posts Container with Horizontal Scrolling */}
			<div
				className="flex w-full flex-row gap-x-[55px] overflow-x-auto px-1 pb-3 pt-6"
				ref={scrollContainerRef}
				sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{/* Post Previews for Selected/All Platforms */}
				{Object.entries(displayedPlatforms).map(([platform, posts]) => {
					const platformType = platform as PlatformType;
					const isLoadingPlatform = data.loadingStates[platformType];
					const platformLabel = PLATFORM_LABELS[platformType];

					// Show platform section if:
					// 1. Platform has posts, OR
					// 2. Platform is currently loading (show skeleton)
					const shouldShowPlatform = posts.length > 0 || isLoadingPlatform;

					if (!shouldShowPlatform) {
						return null;
					}

					return (
						<section
							key={platform}
							className="flex gap-3"
							data-testid={`platform-posts-${platform}`}
							aria-label={`${platformLabel} posts`}
						>
							{/* Show actual posts if available */}
							{posts.length > 0 ? (
								posts.map((post: ReadyToSharePost) => {
									const variant = getPostVariant(platform);
									const postDisabled = isPostDisabled(post);
									const handlePostClick = !postDisabled
										? () => {
												const sharingProps = mapToSharingModalProps(post, platformType, petition);
												startSharingFlow(sharingProps);
											}
										: undefined;
									const buttonText = getButtonText(platformType, postDisabled);

									return (
										<div key={post.id} className="flex flex-col gap-3 gap-y-6">
											<PostPreview
												variant={variant}
												sharingResource={{
													message: extractMessage(post.message),
													previewImageUrl: post.images[0]?.url || '',
													petitionTitle: petition.ask,
													shareUrl: 'www.change.org/:slug',
													recommendedGroup: {
														name: 'r/coffee',
														url: '',
														metadata: { memberCount: 99, imageUrl: '', source: '' },
													},
												}}
												size="large"
												loading={isLoadingPlatform}
												showPlatformIcon
												data-testid={`post-preview-${platform}`}
												petition={petition}
												disabled={postDisabled}
												onClick={handlePostClick}
											/>
											<Button
												onClick={handlePostClick}
												variant="secondary"
												size="small"
												disabled={postDisabled}
												data-qa={`share-button-${platform}`}
												data-testid={`share-button-${platform}`}
												aria-label={buttonText}
												className="w-full"
											>
												{buttonText}
											</Button>
										</div>
									);
								})
							) : (
								// Show skeleton while loading
								<PostPreview
									key={`${platform}-skeleton`}
									variant={getPostVariant(platform)}
									sharingResource={{
										message: '',
										previewImageUrl: '',
										petitionTitle: petition.ask,
										shareUrl: '',
										recommendedGroup: { name: '', url: '', metadata: { memberCount: 0, imageUrl: '', source: '' } },
									}}
									size="large"
									loading
									showPlatformIcon
									data-testid={`post-preview-${platform}-skeleton`}
									petition={petition}
									disabled={false}
								/>
							)}
						</section>
					);
				})}
			</div>
		</section>
	);
}
